import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from "react-router-dom";
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPackage, FiTruck, FiCalendar, FiArrowLeft, FiEdit, FiMapPin, 
  FiUser, FiCreditCard, FiLink, FiShoppingBag, FiCheck, FiMail, 
  FiSave, FiAlertCircle, FiX, FiMap, FiBarChart2, FiEye
} from 'react-icons/fi';
import { TextField, FormControl, InputLabel, Select, MenuItem, Modal, Box } from '@mui/material';

import Amex from "../../assets/img/amex.svg";
import Mastercard from "../../assets/img/mastercard.svg";
import Visa from "../../assets/img/visa.svg";
import AdminLayout from '../../components/ui/AdminLayout';
import { withAuthAdmin } from '../../hooks/useAuthAdmin';

const brandLogos = {
  amex: Amex,
  mastercard: Mastercard,
  visa: Visa
};

function OrderDetailsAdmin() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedOrder, setEditedOrder] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [trackingInfo, setTrackingInfo] = useState({
    trackingNumber: '',
    carrier: 'DHL',
    estimatedDelivery: ''
  });

  // Fetch order data
  useEffect(() => {
    async function fetchOrderData() {
      try {
        const adminToken = Cookies.get('adminKey');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/order/${orderId}`, {
          method: 'GET',
          headers: {
            "Admin-Token": adminToken
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch order data');
        }

        const data = await response.json();
        setOrder(data);
        setEditedOrder(data); // Initialize edited order with the original data
        setLoading(false);
      } catch (error) {
        setError('There was a problem retrieving this order. Please try again later.');
        setLoading(false);
      }
    }

    fetchOrderData();
  }, [orderId]);

  useEffect(() => {
    async function markOrderAsSeen() {
      if (order && order.id && order.adminViewed === false) {
        try {
          const adminToken = Cookies.get('adminKey');
          await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/markOrderAsSeen`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Admin-Token': adminToken
            },
            body: JSON.stringify({
              orderId: order.id
            })
          });
          
          // Update local state to reflect the change
          setOrder(prev => ({
            ...prev,
            adminViewed: true
          }));
        } catch (error) {
          console.error('Error marking order as seen:', error);
        }
      }
    };
    markOrderAsSeen();
  }, [order]);

  // Save edited order
  const handleSaveOrder = async () => {
    setSaveLoading(true);
    try {
      const adminToken = Cookies.get('adminKey');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/updateOrder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Admin-Token': adminToken
        },
        body: JSON.stringify({
          orderId: editedOrder.id,
          status: editedOrder.status,
          adminShipped: editedOrder.adminShipped === true,
          adminViewed: editedOrder.adminViewed === true,
          shippingAddress: editedOrder.shippingAddress
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update order');
      }

      // Update local state
      setOrder(editedOrder);
      setEditMode(false);
      setSaveLoading(false);
    } catch (error) {
      console.error('Error saving order:', error);
      setSaveLoading(false);
    }
  };

  // Update order tracking info
  const handleSaveTracking = async () => {
    setSaveLoading(true);
    try {
      const adminToken = Cookies.get('adminKey');
      
      // Generate tracking URL based on carrier
      let trackingUrl;
      switch (trackingInfo.carrier) {
        case 'DHL':
          trackingUrl = `https://www.dhl.com/it-it/home/tracking/tracking-express.html?submit=1&tracking-id=${trackingInfo.trackingNumber}`;
          break;
        case 'BRT':
          trackingUrl = `https://mybrt.net/tracking.do?parcelCode=${trackingInfo.trackingNumber}`;
          break;
        case 'GLS':
          trackingUrl = `https://www.gls-italy.com/it/servizi-per-destinatario/tracking-spedizioni?match=${trackingInfo.trackingNumber}`;
          break;
        case 'UPS':
          trackingUrl = `https://www.ups.com/track?tracknum=${trackingInfo.trackingNumber}`;
          break;
        case 'FedEx':
          trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${trackingInfo.trackingNumber}`;
          break;
        case 'Poste Italiane':
          trackingUrl = `https://www.poste.it/cerca/index.html#/risultati-spedizioni/${trackingInfo.trackingNumber}`;
          break;
        case 'SDA':
          trackingUrl = `https://www.sda.it/wps/portal/Servizi_online/ricerca_spedizioni?locale=it&tracing.letteraVettura=${trackingInfo.trackingNumber}`;
          break;
        default:
          trackingUrl = `https://www.google.com/search?q=${trackingInfo.carrier}+tracking+${trackingInfo.trackingNumber}`;
      }
      
      // Format date as ISO string for Java's Date type compatibility
      const formattedDeliveryDate = trackingInfo.estimatedDelivery ? 
        new Date(trackingInfo.estimatedDelivery).toISOString() : null;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/updateTrackingData`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Admin-Token': adminToken
        },
        body: JSON.stringify({
          orderId: order.id,
          trackingNumber: trackingInfo.trackingNumber,
          courier: trackingInfo.carrier,
          trackingUrl: trackingUrl,
          expectedDeliveryDate: formattedDeliveryDate
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update tracking information');
      }

      const updatedOrder = await response.json();
      
      // Update local state with the returned data from server
      setOrder(updatedOrder);
      
      // If the order was in pending or paid status, automatically update to shipped
      if (order.status === 'pending' || order.status === 'paid') {
        // Update status to "shipped"
        await handleStatusChange('shipped', order.id);
      }
      
      setShowShippingModal(false);
      setSaveLoading(false);
      
    } catch (error) {
      console.error('Error saving tracking info:', error);
      setSaveLoading(false);
    }
  };

  // Helper function to update order status
  const handleStatusChange = async (newStatus, orderIdToUpdate) => {
    try {
      const adminToken = Cookies.get('adminKey');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}admin/updateOrder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Admin-Token': adminToken
        },
        body: JSON.stringify({
          orderId: orderIdToUpdate,
          status: newStatus,
          adminShipped: "Yes"
        })
      });
      
      if (response.ok) {
        // Update local state
        setOrder(prev => ({
          ...prev,
          status: newStatus,
          adminShipped: "Yes"
        }));
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'paid': 'bg-green-100 text-green-800 border-green-200',
      'processing': 'bg-blue-100 text-blue-800 border-blue-200',
      'shipped': 'bg-purple-100 text-purple-800 border-purple-200',
      'delivered': 'bg-teal-100 text-teal-800 border-teal-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200',
      'default': 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium border ${statusStyles[status] || statusStyles.default}`}>
        {status}
      </span>
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96 w-full">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-t-4 border-b-4 border-[#406580] rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">Caricamento ordine...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96 w-full px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md text-center">
            <FiAlertCircle className="text-red-500 text-4xl mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-700 mb-2">Errore</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => navigate('/admin?component=orders')}
              className="px-4 py-2 bg-[#406580] text-white rounded-lg hover:bg-[#305570]"
            >
              Torna agli Ordini
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96 w-full px-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md text-center">
            <FiPackage className="text-yellow-500 text-4xl mx-auto mb-4" />
            <h2 className="text-xl font-bold text-yellow-700 mb-2">Ordine non trovato</h2>
            <p className="text-yellow-600 mb-4">L'ordine richiesto non è disponibile.</p>
            <button 
              onClick={() => navigate('/admin?component=orders')}
              className="px-4 py-2 bg-[#406580] text-white rounded-lg hover:bg-[#305570]"
            >
              Torna agli Ordini
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Parse payment method data
  let cardInfo = null;
  try {
    const paymentMethodData = order.paymentMethodData ? JSON.parse(order.paymentMethodData) : null;
    if (paymentMethodData && paymentMethodData.brand && paymentMethodData.last4) {
      cardInfo = {
        brand: paymentMethodData.brand,
        last4: paymentMethodData.last4,
        expMonth: paymentMethodData.exp_month,
        expYear: paymentMethodData.exp_year
      };
    }
  } catch (error) {
    console.error('Error parsing payment method data:', error);
  }

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: "easeIn" }
    }
  };

  return (
    <div className="bg-white">
      <AdminLayout>
        <motion.section 
          initial="hidden" 
          animate="visible" 
          variants={containerVariants} 
          className="w-full mt-1 max-w-[104rem] px-6 py-4"
        >
          {/* Header with back button */}
          <motion.div variants={itemVariants} className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/admin?component=orders')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors flex items-center"
              >
                <FiArrowLeft size={20} className="text-gray-600" />
              </button>
              <div>
                <div className="flex items-center">
                  <h1 className="text-2xl font-bold text-gray-800">Dettagli Ordine</h1>
                  <span className="ml-2 bg-gray-100 px-2 py-1 rounded-lg text-gray-700 font-mono">#{order.id}</span>
                </div>
                <div className="flex items-center mt-1 text-sm text-gray-600">
                  <Link to="/admin?component=orders" className="hover:text-[#406580]">Ordini</Link>
                  <span className="mx-2">/</span>
                  <span>Dettaglio #{order.id}</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              {!editMode ? (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setEditMode(true)}
                    className="flex items-center px-3 py-1.5 bg-[#406580] text-white rounded-lg text-sm font-medium hover:bg-[#305570] transition-colors"
                  >
                    <FiEdit className="mr-1" size={16} />
                    Modifica
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowShippingModal(true)}
                    className="flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                    disabled={order.status === 'cancelled'}
                  >
                    <FiTruck className="mr-1" size={16} />
                    Aggiorna Spedizione
                  </motion.button>

                  <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    href={`mailto:customer@example.com?subject=Il tuo ordine #${order.id}`}
                    className="flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                  >
                    <FiMail className="mr-1" size={16} />
                    Contatta
                  </motion.a>
                </>
              ) : (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setEditedOrder(order);
                      setEditMode(false);
                    }}
                    className="flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                  >
                    <FiX className="mr-1" size={16} />
                    Annulla
                  </motion.button>
                  
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleSaveOrder}
                    disabled={saveLoading}
                    className="flex items-center px-3 py-1.5 bg-[#406580] text-white rounded-lg text-sm font-medium hover:bg-[#305570] transition-colors"
                  >
                    {saveLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <FiSave className="mr-1" size={16} />
                        Salva Modifiche
                      </>
                    )}
                  </motion.button>
                </>
              )}
            </div>
          </motion.div>

          {/* Order Summary Card */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-wrap justify-between">
              <div className="flex items-center mb-4 sm:mb-0">
                <div className="w-12 h-12 bg-[#406580] rounded-full flex items-center justify-center text-white mr-3">
                  <FiPackage size={24} />
                </div>
                <div>
                  <div className="flex items-center">
                    <h2 className="text-xl font-bold text-gray-800 mr-3">Ordine #{order.id}</h2>
                    {getStatusBadge(order.status)}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Creato il {formatDate(order.orderCreationTime)}</p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Totale Ordine</p>
                  <p className="text-2xl font-bold text-gray-800">€{order.paymentTotal?.toFixed(2)}</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Customer & Shipping Info */}
            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <FiUser className="text-[#406580] mr-2" size={18} />
                <h3 className="text-lg font-semibold text-gray-800">Informazioni Cliente</h3>
              </div>

              {editMode ? (
                <TextField
                  label="Indirizzo di Spedizione"
                  fullWidth
                  multiline
                  rows={3}
                  value={editedOrder.shippingAddress || ''}
                  onChange={(e) => setEditedOrder({ ...editedOrder, shippingAddress: e.target.value })}
                  className="mb-4"
                />
              ) : (
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-1">Indirizzo di Spedizione</p>
                  <p className="text-gray-800">{order.shippingAddress}</p>
                  
                  <div className="flex items-center mt-3">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => navigator.clipboard.writeText(order.shippingAddress)}
                      className="text-blue-600 text-sm flex items-center mr-3 hover:underline"
                    >
                      <FiLink className="mr-1" size={14} />
                      Copia
                    </motion.button>
                    
                    <motion.a
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.shippingAddress)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 text-sm flex items-center hover:underline"
                    >
                      <FiMap className="mr-1" size={14} />
                      Vedi sulla mappa
                    </motion.a>
                  </div>
                </div>
              )}

              {editMode ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormControl fullWidth>
                    <InputLabel id="viewed-select-label">Visualizzato</InputLabel>
                    <Select
                      labelId="viewed-select-label"
                      value={editedOrder.adminViewed}
                      label="Visualizzato"
                      onChange={(e) => setEditedOrder({ ...editedOrder, adminViewed: e.target.value })}
                    >
                      <MenuItem value="Yes">Si</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel id="shipped-select-label">Spedito</InputLabel>
                    <Select
                      labelId="shipped-select-label"
                      value={editedOrder.adminShipped}
                      label="Spedito"
                      onChange={(e) => setEditedOrder({ ...editedOrder, adminShipped: e.target.value })}
                    >
                      <MenuItem value="Yes">Si</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              ) : (
                <div className="flex space-x-6 mt-4">
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-2 ${order.adminViewed === true ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <span className="text-sm text-gray-600">Visualizzato</span>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-2 ${order.adminShipped === true ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <span className="text-sm text-gray-600">Spedito</span>
                  </div>
                </div>
              )}

              {order.trackingNumber && (
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center mb-3">
                    <FiTruck className="text-[#406580] mr-2" size={18} />
                    <h3 className="text-lg font-semibold text-gray-800">Informazioni Spedizione</h3>
                  </div>
                  
                  <div className="text-sm">
                    <p className="mb-1 text-gray-500">Corriere</p>
                    <p className="font-medium mb-3">{order.trackingCarrier || 'Non specificato'}</p>
                    
                    <p className="mb-1 text-gray-500">Numero Tracciamento</p>
                    <div className="flex items-center">
                      <p className="font-medium font-mono">{order.trackingNumber}</p>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigator.clipboard.writeText(order.trackingNumber)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <FiLink size={14} />
                      </motion.button>
                    </div>
                    
                    {order.estimatedDelivery && (
                      <>
                        <p className="mt-3 mb-1 text-gray-500">Consegna Stimata</p>
                        <p className="font-medium">{formatDate(order.estimatedDelivery)}</p>
                      </>
                    )}

                    <a 
                      href={`https://www.${order.trackingCarrier?.toLowerCase() || 'dhl'}.com/track?trackingNumber=${order.trackingNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center mt-3 text-blue-600 hover:underline"
                    >
                      <FiBarChart2 className="mr-1" />
                      Traccia spedizione
                    </a>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Payment Info */}
            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <FiCreditCard className="text-[#406580] mr-2" size={18} />
                <h3 className="text-lg font-semibold text-gray-800">Dettagli Pagamento</h3>
              </div>

              {editMode ? (
                <FormControl fullWidth className="mb-4">
                  <InputLabel id="status-select-label">Stato Ordine</InputLabel>
                  <Select
                    labelId="status-select-label"
                    value={editedOrder.status}
                    label="Stato Ordine"
                    onChange={(e) => setEditedOrder({ ...editedOrder, status: e.target.value })}
                  >
                    <MenuItem value="pending">In attesa</MenuItem>
                    <MenuItem value="paid">Pagato</MenuItem>
                    <MenuItem value="processing">In lavorazione</MenuItem>
                    <MenuItem value="shipped">Spedito</MenuItem>
                    <MenuItem value="delivered">Consegnato</MenuItem>
                    <MenuItem value="cancelled">Annullato</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-1">Stato Pagamento</p>
                  <div>{getStatusBadge(order.status)}</div>
                  
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 mb-1">Data Pagamento</p>
                    <p className="flex items-center text-gray-800">
                      <FiCalendar className="mr-1 text-gray-500" />
                      {formatDate(order.paymentExecutionTime)}
                    </p>
                  </div>
                </div>
              )}

              {cardInfo && (
                <div className="mb-6">
                  <p className="text-sm text-gray-500 mb-2">Metodo di Pagamento</p>
                  <div className="flex items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                    {brandLogos[cardInfo.brand] && (
                      <img 
                        src={brandLogos[cardInfo.brand]} 
                        alt={cardInfo.brand} 
                        className="h-8 w-auto mr-3" 
                      />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">•••• {cardInfo.last4}</p>
                      <p className="text-xs text-gray-500">Scadenza: {cardInfo.expMonth}/{cardInfo.expYear}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500 mb-2">ID Pagamento</p>
                <a
                  href={`https://dashboard.stripe.com/payments/${order.stripePaymentId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start font-mono text-sm text-blue-600 hover:underline break-all"
                >
                  <FiLink className="mr-1 mt-1 flex-shrink-0" />
                  <span>{order.stripePaymentId}</span>
                </a>
              </div>
            </motion.div>

            {/* Order Summary */}
            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <FiShoppingBag className="text-[#406580] mr-2" size={18} />
                <h3 className="text-lg font-semibold text-gray-800">Riepilogo Ordine</h3>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotale</span>
                  <span>€{(order.paymentTotal - 5.99).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Spedizione</span>
                  <span>€5.99</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>IVA (22%)</span>
                  <span>€{(order.paymentTotal * 0.22).toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-semibold text-lg text-gray-900 pt-2 border-t border-gray-200">
                  <span>Totale</span>
                  <span>€{order.paymentTotal.toFixed(2)}</span>
                </div>
              </div>

              <div>
                <Link 
                  to={`/order/${order.stripePaymentId}`} 
                  target="_blank"
                  className="flex items-center justify-center w-full px-4 py-2 mt-4 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  <FiEye className="mr-2" />
                  Visualizza come Cliente
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Order Items */}
          <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Prodotti nell'Ordine</h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prodotto
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Prezzo
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nome Prodotto
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {order.orderProducts && order.orderProducts.map((product, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-12 w-12 bg-gray-100 rounded-md overflow-hidden flex items-center justify-center">
                            {product.imageUrl ? (
                              <img 
                                src={product.imageUrl?.startsWith('http') ? product.imageUrl : 
                                      `https://res.cloudinary.com/dndm9hex2/image/upload/v1733331162/${product.imageUrl}`}
                                alt={product.title} 
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <FiPackage className="text-gray-400" size={20} />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{product.title}</div>
                            <div className="text-sm text-gray-500">{product.variant || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">{product.sku}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        €{product.productPrice?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                        {product.productName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Shipping Tracking Modal */}
          <AnimatePresence>
            {showShippingModal && (
              <Modal
                open={showShippingModal}
                onClose={() => setShowShippingModal(false)}
                closeAfterTransition
                BackdropComponent={({ children }) => (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowShippingModal(false)}
                    className="fixed inset-0 bg-black/50 z-40 backdrop-blur-sm"
                  >
                    {children}
                  </motion.div>
                )}
              >
                <div>
                  <motion.div
                    variants={modalVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="absolute top-[185px] left-1/2 -ml-[300px] bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto z-[50]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-[#406580] rounded-full flex items-center justify-center text-white mr-3">
                          <FiTruck size={20} />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-800">Aggiornamento Spedizionef</h2>
                      </div>
                      <button
                        onClick={() => setShowShippingModal(false)}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <FiX size={24} />
                      </button>
                    </div>

                    <div className="space-y-4 mb-6">
                      <TextField
                        label="Numero di Tracciamento"
                        fullWidth
                        value={trackingInfo.trackingNumber}
                        onChange={(e) => setTrackingInfo({...trackingInfo, trackingNumber: e.target.value})}
                        required
                      />

                      <FormControl fullWidth>
                        <InputLabel>Corriere</InputLabel>
                        <Select
                          value={trackingInfo.carrier}
                          label="Corriere"
                          onChange={(e) => setTrackingInfo({...trackingInfo, carrier: e.target.value})}
                        >
                          <MenuItem value="DHL">DHL</MenuItem>
                          <MenuItem value="BRT">BRT</MenuItem>
                          <MenuItem value="GLS">GLS</MenuItem>
                          <MenuItem value="UPS">UPS</MenuItem>
                          <MenuItem value="FedEx">FedEx</MenuItem>
                          <MenuItem value="Poste Italiane">Poste Italiane</MenuItem>
                          <MenuItem value="SDA">SDA</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        label="Data Consegna Stimata"
                        type="date"
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                        value={trackingInfo.estimatedDelivery}
                        onChange={(e) => setTrackingInfo({...trackingInfo, estimatedDelivery: e.target.value})}
                      />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => setShowShippingModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                      >
                        Annulla
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={handleSaveTracking}
                        disabled={saveLoading || !trackingInfo.trackingNumber}
                        className="px-4 py-2 bg-[#406580] text-white rounded-lg font-medium hover:bg-[#305570] transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                      >
                        {saveLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Salvando...
                          </>
                        ) : (
                          <>
                            <FiSave className="mr-2" />
                            Aggiorna Spedizione
                          </>
                        )}
                      </motion.button>
                    </div>
                  </motion.div>
                </div>
              </Modal>
            )}
          </AnimatePresence>
        </motion.section>
      </AdminLayout>
    </div>
  );
}

export default withAuthAdmin(OrderDetailsAdmin);